import * as Sentry from '@sentry/react';
import { config } from '../config/environment';
import { StoreScorecardEntry } from '../data/scorecardData';
import { getAuthToken, authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';

export interface ManagementUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  location_id?: string;
  role: string;
  tenant_id?: string;
  preferred_locale?: string;
}

/**
 * feat-324 — why a management-user lookup did or did not yield a user.
 *  - `ok`:        200 `success:true` with a user.
 *  - `not_found`: the backend said definitively there is no record (200 `success:false`
 *                 "No management user found…") or the principal is not provisioned (HTTP 403).
 *  - `failed`:    anything else — network/throw, other non-OK status, other `success:false`.
 * Drives the route guards' "We couldn't load your account" vs "Access denied" card.
 */
export type ManagementLookupOutcome = 'ok' | 'not_found' | 'failed';

export interface ManagementUserResponse {
  success: boolean;
  user?: ManagementUser;
  error?: string;
  /** feat-324 — set by getManagementUserByEmail only; additive. */
  outcome?: ManagementLookupOutcome;
}

/**
 * feat-324 — prefix of the backend's "no record" message.
 * COUPLED TO BACKEND TEXT: restaurant-backend/routes/management.py
 * `get_management_user_by_email` returns 200 `{success: false, error: "No management user
 * found with email: …"}` for a missing row — and the SAME 200 `success:false` shape with
 * "Internal server error: …" for a DB exception. Only this prefix distinguishes the two.
 * If the backend message changes, a missing user will be shown as a load failure
 * (Variant A) instead of "Access denied". Pinned by managementService.feat324.test.ts.
 */
export const NO_MANAGEMENT_USER_PREFIX = 'No management user found';

export function classifyManagementLookup(
  status: number,
  body: ManagementUserResponse | null,
): ManagementLookupOutcome {
  if (status === 403) return 'not_found';
  if (status < 200 || status >= 300 || !body) return 'failed';
  if (body.success && body.user) return 'ok';
  if (
    body.success === false &&
    typeof body.error === 'string' &&
    body.error.startsWith(NO_MANAGEMENT_USER_PREFIX)
  ) {
    return 'not_found';
  }
  return 'failed';
}

export interface Shift {
  id: string;
  manager_id: string;
  created: string;
  ended?: string;
  status?: string;
  score?: number;
  server_count?: number;
  shift_date?: string;
  server_name?: string;
  greetings?: number;
  /** @deprecated use metric_percents[<key>] keyed by the tenant's metric_keys. */
  premium_menu?: number;
  /** @deprecated use metric_percents[<key>]. */
  item_mention?: number;
  /** @deprecated use metric_percents[<key>]. */
  item_description?: number;
  drink_promotions?: number;
  /** @deprecated use metric_counts[<key>]. */
  upsell_count?: number;
  /** @deprecated use metric_counts[<key>]. */
  mention_count?: number;
  /** @deprecated use metric_counts[<key>]. */
  describe_count?: number;
  drink_promotion_count?: number;
  server_shift_id?: string;
  role?: string;
  prompt_template_version_id?: string;
  /** Per-tenant metric counts keyed by metric_key, from the active prompt. */
  metric_counts?: Record<string, number>;
  /** Per-tenant metric percentages keyed by metric_key (0..100). */
  metric_percents?: Record<string, number>;
  /** Per-tenant metric display labels keyed by metric_key. */
  metric_labels?: Record<string, string>;
  /** Drink promotion column label (null if disabled for this tenant). */
  drink_label?: string | null;
}

export interface ShiftsResponse {
  success: boolean;
  shifts?: Shift[];
  count?: number;
  error?: string;
}

/**
 * Client for `/api/management/*`.
 *
 * Auth (feat-221, 2026-08-05): every route this class calls requires a bearer token.
 * Four call sites here used to carry `// Note: Management endpoints don't require
 * authentication` — true when written, false as of feat-221, and the requests were
 * authenticated the whole time anyway because `authedApiFetch` (`tokenRefresh.ts`)
 * injects the header centrally. They are deleted rather than corrected in place; one
 * statement is harder to leave stale than four copies.
 *
 * Most of these routes are now `Depends(require_any_manager_or_admin)` on the backend —
 * i.e. role admin | corporate | manager — so a 403 here is an authorisation result, not
 * a bug. Pages that call them must be mounted under `StaffRoute`, whose admitted set is
 * exactly that one; several methods below swallow failures into empty results, which
 * turns a denial into a blank screen if the route guard is wrong.
 */
class ManagementService {
  /**
   * Helper function to build the correct API URL based on environment
   */
  private buildApiUrl(path: string): string {
    const protocol = config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
    return `${protocol}://${config.restaurantBackendHost}${path}`;
  }

  /**
   * Get management user details by email
   */
  async getManagementUserByEmail(email: string): Promise<ManagementUserResponse> {
    try {
      const url = `/api/management/user/${encodeURIComponent(email)}`;
      const fullUrl = this.buildApiUrl(url);
      console.log('ManagementService - Making request to:', fullUrl);
      
      const accessToken = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Include access token if available
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await authedApiFetch(fullUrl, {
        headers,
      });
      console.log('ManagementService - Response status:', response.status);
      console.log('ManagementService - Response headers:', response.headers);
      
      if (!response.ok) {
        const text = await response.text();
        console.log('ManagementService - Error response text:', text);
        // feat-324: 403 = principal not provisioned → not_found; other non-OK → failed.
        if (response.status === 403) {
          return {
            success: false,
            error: `HTTP error! status: ${response.status}`,
            outcome: 'not_found',
          };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: ManagementUserResponse = await response.json();
      console.log('ManagementService - Response data:', data);
      return { ...data, outcome: classifyManagementLookup(response.status, data) };
    } catch (error) {
      console.error('Error fetching management user:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: 'Failed to fetch management user details',
        outcome: 'failed',
      };
    }
  }

  /**
   * Get management user details by user ID
   */
  async getManagementUserById(userId: string): Promise<ManagementUserResponse> {
    try {
      const url = `/api/management/user-by-id/${encodeURIComponent(userId)}`;
      const fullUrl = this.buildApiUrl(url);
      console.log('ManagementService - Making request to:', fullUrl);
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('ManagementService - Response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching management user by ID:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: 'Failed to fetch management user by ID'
      };
    }
  }

  /**
   * Get all management users (for debugging/admin purposes)
   */
  async getAllManagementUsers(): Promise<{ success: boolean; users?: ManagementUser[]; count?: number; error?: string }> {
    try {
      const fullUrl = this.buildApiUrl('/api/management/users');
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching management users:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: 'Failed to fetch management users'
      };
    }
  }

  /**
   * Get shifts for a specific manager
   */
  async getShiftsByManagerId(managerId: string, tenantId?: string): Promise<ShiftsResponse> {
    try {
      const params = new URLSearchParams();
      if (tenantId) params.append('tenant_id', tenantId);
      const queryString = params.toString();
      const fullUrl = this.buildApiUrl(`/api/management/shifts/${encodeURIComponent(managerId)}${queryString ? '?' + queryString : ''}`);
      console.log('ManagementService - Fetching shifts for manager:', managerId, 'tenantId:', tenantId);
      console.log('ManagementService - Making request to:', fullUrl);
      
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        }
      });

      console.log('ManagementService - Shifts response status:', response.status);
      
      if (!response.ok) {
        const text = await response.text();
        console.log('ManagementService - Error response text:', text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('ManagementService - Shifts response data:', data);
      return data;
    } catch (error) {
      console.error('Error fetching shifts for manager:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: 'Failed to fetch shifts for manager'
      };
    }
  }

  /**
   * Get user initials from first and last name
   */
  getInitials(firstName: string, lastName: string): string {
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  /**
   * Get full name from first and last name
   */
  getFullName(firstName: string, lastName: string): string {
    return `${firstName} ${lastName}`.trim();
  }

  /**
   * Get servers, optionally filtered by location_id
   */
  async getServers(locationId?: string, tenantId?: string): Promise<{ success: boolean; servers?: any[]; count?: number; error?: string }> {
    try {
      if (!locationId) {
        // If no location ID provided, return empty array
        return {
          success: true,
          servers: [],
          count: 0
        };
      }

      const token = getAuthToken();
      if (!token) {
        return {
          success: false,
          error: 'Authentication required'
        };
      }

          // Use the tenant-specific endpoint if tenantId is provided
          if (tenantId) {
            const url = `/api/tenant/${encodeURIComponent(tenantId)}/locations/${encodeURIComponent(locationId)}/servers`;
            const fullUrl = this.buildApiUrl(url);
            
            const response = await authedApiFetch(fullUrl, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              }
            });
            
            if (!response.ok) {
              // Fall back to management endpoint
            } else {
              const data = await response.json();
              return {
                success: true,
                servers: data,
                count: data.length
              };
            }
          }

          // Fallback to management endpoint
          const url = `/api/management/servers?location_id=${encodeURIComponent(locationId)}`;
          const fullUrl = this.buildApiUrl(url);
      
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      
          if (!response.ok) {
            return {
              success: true,
              servers: [],
              count: 0
            };
          }

          const data = await response.json();
          return data;
    } catch (error) {
      console.error('Error fetching servers:', error);
      Sentry.captureException(error);
      return {
        success: true,
        servers: [],
        count: 0
      };
    }
  }

  /**
   * Get all locations from the database
   */
  async getLocations(): Promise<{ success: boolean; locations?: any[]; count?: number; error?: string }> {
    try {
      const fullUrl = this.buildApiUrl('/api/management/locations');
      console.log('ManagementService - Fetching locations:', fullUrl);
      
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('ManagementService - Locations response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: 'Failed to fetch locations'
      };
    }
  }

  async getStoreScorecardData(locationId?: string, startDate?: string, endDate?: string, tenantId?: string): Promise<{ success: boolean; entries?: StoreScorecardEntry[]; count?: number; error?: string }> {
    try {
      const params = new URLSearchParams();
      if (locationId) params.append('location_id', locationId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const fullUrl = this.buildApiUrl(`/api/management/store-scorecard?${params.toString()}`);
      console.log('ManagementService - Fetching store scorecard data:', fullUrl, 'tenantId:', tenantId);
      
      const accessToken = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Include access token if available
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const response = await authedApiFetch(fullUrl, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ManagementService - Store scorecard response:', data);
      return data;
    } catch (error) {
      console.error('Error fetching store scorecard data:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: 'Failed to fetch store scorecard data'
      };
    }
  }

  /**
   * Get all tenants
   */
  async getTenants(): Promise<{ success: boolean; tenants?: any[]; count?: number; error?: string }> {
    try {
      const token = getAuthToken();
      if (!token) {
        console.error('ManagementService - No token found for getTenants');
        return {
          success: false,
          error: 'Authentication required. Please log in again.'
        };
      }
      
      // Try the /api/tenant route first (with auth)
      const newUrl = this.buildApiUrl('/api/tenant');
      console.log('ManagementService - Fetching tenants (new route):', newUrl);
      
      const newResponse = await authedApiFetch(newUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (newResponse.ok) {
        const data = await newResponse.json();
        console.log('ManagementService - Tenants response (new route):', data);
        return { success: true, tenants: data, count: data.length };
      }
      
      // ⚠️ Fall back to the old /api/management/tenants route. THIS IS STILL A
      // PRIVILEGE-ESCALATION PATH. Do not read the feat-221 gating as having closed it.
      //
      // An earlier version of this comment said the two routes are "the same class of
      // gate" and that the escalation "was" a past problem. That was WRONG, and it was
      // corrected in the wrong direction — caught by the feat-221 security review (M3).
      //
      // feat-221 made this route AUTHENTICATED, not SCOPED. The two are different:
      //   /api/tenant                -> role-filtered, default-deny: returns only the
      //                                 tenants this caller may see.
      //   /api/management/tenants    -> Depends(require_any_manager_or_admin) and then
      //                                 `SELECT ... FROM tenant ORDER BY tenant_name`
      //                                 (routes/management.py:1239) — EVERY tenant, to
      //                                 ANY manager. Gated, unbounded.
      //
      // So on a 5xx of /api/tenant a single-tenant manager still falls through to the
      // full tenant list. The arm is a CONSUMER of the Phase-2 scoping gap (ADR 0001 D4),
      // not the defect itself — which is why it was deliberately NOT deleted: removing one
      // consumer would let the fix read as "handled" while the unscoped route stayed live
      // for every other caller, present and future. Scoping the route closes it at source.
      //
      // Delete this arm only AFTER Phase 2 lands, at which point it becomes genuinely
      // inert and removable as ordinary cleanup rather than as a security change.
      console.warn(`New tenant API route failed with status ${newResponse.status}. Falling back to old route.`);
      const oldUrl = this.buildApiUrl('/api/management/tenants');
      console.log('ManagementService - Fetching tenants (old route):', oldUrl);
      
      const oldResponse = await authedApiFetch(oldUrl, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!oldResponse.ok) {
        // Try to get error message from response
        const errorData = await oldResponse.json().catch(() => ({ detail: 'Unknown error' }));
        const errorMessage = errorData.detail || `HTTP error! status: ${oldResponse.status}`;
        throw new Error(errorMessage);
      }
      
      const data = await oldResponse.json();
      console.log('ManagementService - Tenants response (old route):', data);
      return data;
    } catch (error: any) {
      console.error('Error fetching tenants:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: error.message || 'Failed to fetch tenants'
      };
    }
  }

  /**
   * Get a specific tenant by ID
   */
  async getTenantById(tenantId: string): Promise<{ success: boolean; tenant?: any; error?: string }> {
    try {
      const url = `/api/management/tenants/${encodeURIComponent(tenantId)}`;
      const fullUrl = this.buildApiUrl(url);
      console.log('ManagementService - Fetching tenant by ID:', fullUrl);
      
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        const errorMessage = errorData.detail || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      console.log('ManagementService - Tenant by ID response:', data);
      return data;
    } catch (error: any) {
      console.error('Error fetching tenant by ID:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: error.message || 'Failed to fetch tenant by ID'
      };
    }
  }

  /**
   * Get locations by tenant ID
   */
  async getLocationsByTenant(tenantId?: string): Promise<{ success: boolean; locations?: any[]; count?: number; error?: string }> {
    try {
      if (!tenantId) {
        return {
          success: false,
          error: 'Tenant ID is required to fetch locations'
        };
      }

      // Use the new tenant management endpoint: /api/tenant/{tenant_id}/locations
      const url = `/api/tenant/${encodeURIComponent(tenantId)}/locations`;
      const fullUrl = this.buildApiUrl(url);
      console.log('ManagementService - Fetching locations by tenant:', fullUrl);
      
      const response = await authedApiFetch(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeader(),
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        const errorMessage = errorData.detail || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('ManagementService - Locations by tenant response:', data);
      
      // The endpoint returns an array directly, so wrap it in a success response
      return {
        success: true,
        locations: data,
        count: data.length
      };
    } catch (error: any) {
      console.error('Error fetching locations by tenant:', error);
      Sentry.captureException(error);
      return {
        success: false,
        error: error.message || 'Failed to fetch locations by tenant'
      };
    }
  }
}

export default new ManagementService();
